import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageSection, StatusBadge } from "../components/reporting";
import { useScreenInit } from "../useScreenInit";
import { api } from "../api";

const inputCls =
  "w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors";

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function Field({
  label,
  hint,
  htmlFor,
  required = false,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  htmlFor?: string | undefined;
  required?: boolean;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-caption font-medium text-primary">
          {label}
          {required && <span className="text-status-red"> *</span>}
        </label>
        {hint && <span className="text-micro text-tertiary">{hint}</span>}
      </div>
      {children}
      {error && (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} className="text-micro text-status-red">
          {error}
        </p>
      )}
    </div>
  );
}

function ReadOnlyRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard?.writeText(value);
    } catch {
      /* sandbox clipboard may be unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-9 px-3 rounded-xs border border-default bg-surface-light flex items-center overflow-x-auto scroll-thin">
          <span className="font-mono text-caption text-primary whitespace-nowrap">{value}</span>
        </div>
        <button
          onClick={copy}
          className={`h-9 px-3 rounded-xs border text-caption font-semibold flex items-center gap-2 shrink-0 transition-colors ${
            copied
              ? "border-status-green/40 bg-[rgba(86,205,174,0.12)] text-status-green"
              : "border-default text-button hover:bg-surface-light"
          }`}
        >
          <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"} text-[11px]`} />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </Field>
  );
}

const steps = [
  { n: 1, label: "Brand" },
  { n: 2, label: "Tenant" },
  { n: 3, label: "Inbound" },
  { n: 4, label: "Outbound" },
];

export function AdminBrandSetup() {
  const navigate = useNavigate();
  const seed = useScreenInit();
  const [step, setStep] = useState<number>((seed.step as number) ?? 1);
  const [name, setName] = useState((seed.name as string) ?? "");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [outboundKind, setOutboundKind] = useState<"none" | "sales_email" | "webhook">("none");
  const [outboundLabel, setOutboundLabel] = useState("");
  const [outboundTarget, setOutboundTarget] = useState("");
  const [recipientConfirmed, setRecipientConfirmed] = useState(false);
  const [provisioned, setProvisioned] = useState<{
    tenantId: string;
    webhookUrl: string;
    apiKey: string;
    apiKeyHeader: string;
    owner: { id: string; email: string; emailVerified: boolean };
    outbound: { id: string; kind: "sales_email" | "webhook"; signingSecret?: string } | null;
  } | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [detailsAttempted, setDetailsAttempted] = useState(false);
  const [sendingSetup, setSendingSetup] = useState(false);
  const [setupSent, setSetupSent] = useState(false);

  const trimmedName = name.trim();
  const trimmedOwnerName = ownerName.trim();
  const trimmedOwnerEmail = ownerEmail.trim();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedOwnerEmail);
  const brandDetailsValid = Boolean(trimmedName && trimmedOwnerName && emailValid);
  const nameError = detailsAttempted && !trimmedName ? "Enter a brand name." : "";
  const ownerNameError =
    detailsAttempted && !trimmedOwnerName ? "Enter the brand owner's name." : "";
  const ownerEmailError =
    detailsAttempted && !trimmedOwnerEmail
      ? "Enter the owner's email address."
      : detailsAttempted && !emailValid
        ? "Enter a valid email address."
        : "";
  const outboundTargetValid =
    outboundKind === "sales_email"
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(outboundTarget.trim())
      : outboundKind === "webhook"
        ? isHttpsUrl(outboundTarget.trim())
        : true;
  const outboundValid =
    outboundKind === "none" ||
    Boolean(
      outboundLabel.trim() &&
      outboundTargetValid &&
      (outboundKind !== "sales_email" || recipientConfirmed),
    );

  const slug = (name || "new-brand")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

  const createBrand = async () => {
    if (!brandDetailsValid) {
      setDetailsAttempted(true);
      setStep(1);
      return;
    }
    if (!outboundValid) {
      setStep(4);
      setError("Complete the selected outbound destination or choose Set up later.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const outbound =
        outboundKind === "sales_email"
          ? {
              kind: outboundKind,
              label: outboundLabel.trim(),
              recipient: outboundTarget.trim(),
              authorisedRecipientConfirmation: true as const,
            }
          : outboundKind === "webhook"
            ? {
                kind: outboundKind,
                label: outboundLabel.trim(),
                url: outboundTarget.trim(),
              }
            : undefined;
      setProvisioned(
        await api("/api/admin/tenants", {
          method: "POST",
          body: JSON.stringify({
            name: trimmedName,
            slug,
            ownerName: trimmedOwnerName,
            ownerEmail: trimmedOwnerEmail,
            ...(outbound ? { outbound } : {}),
          }),
        }),
      );
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Brand could not be provisioned");
    } finally {
      setSaving(false);
    }
  };

  const sendAccountSetup = async () => {
    if (!provisioned) return;
    setSendingSetup(true);
    setError("");
    try {
      await api(
        `/api/admin/tenants/${encodeURIComponent(provisioned.tenantId)}/account-setup-email`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
      );
      setSetupSent(true);
    } catch (problem) {
      setError(
        problem instanceof Error ? problem.message : "Account setup email could not be sent",
      );
    } finally {
      setSendingSetup(false);
    }
  };

  const goToStep = (nextStep: number) => {
    if (nextStep > 1 && !brandDetailsValid) {
      setDetailsAttempted(true);
      setStep(1);
      return;
    }
    setStep(nextStep);
  };

  const startAnotherBrand = () => {
    setStep(1);
    setName("");
    setOwnerName("");
    setOwnerEmail("");
    setOutboundKind("none");
    setOutboundLabel("");
    setOutboundTarget("");
    setRecipientConfirmed(false);
    setProvisioned(null);
    setError("");
    setSaving(false);
    setDetailsAttempted(false);
    setSendingSetup(false);
    setSetupSent(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col gap-[24px]">
      {/* Stepper */}
      <div className="bg-surface-white border border-default rounded-xl px-[26px] py-[18px] flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={s.n} className="flex items-center gap-2 flex-1 last:flex-none">
            <button onClick={() => goToStep(s.n)} className="flex items-center gap-2.5 shrink-0">
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center text-caption font-bold transition-colors ${
                  step === s.n
                    ? "bg-brand-red text-on-brand"
                    : step > s.n
                      ? "bg-surface-info text-brand-dark-teal"
                      : "bg-surface-light text-tertiary border border-default"
                }`}
              >
                {step > s.n ? <i className="fa-solid fa-check text-[11px]" /> : s.n}
              </span>
              <span
                className={`text-body font-semibold ${
                  step === s.n ? "text-primary" : "text-tertiary"
                }`}
              >
                {s.label}
              </span>
            </button>
            {i < steps.length - 1 && <span className="flex-1 h-px bg-default" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <PageSection title="Brand details">
          {detailsAttempted && !brandDetailsValid && (
            <div
              role="alert"
              className="mb-4 max-w-[680px] rounded-md border border-status-red/30 bg-[rgba(255,101,80,0.1)] px-4 py-3 text-caption text-status-red"
            >
              Complete all required brand details before continuing.
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 max-w-[680px]">
            <Field label="Brand name" htmlFor="brand-name" required error={nameError}>
              <input
                id="brand-name"
                required
                aria-invalid={Boolean(nameError)}
                aria-describedby={nameError ? "brand-name-error" : undefined}
                className={`${inputCls} ${nameError ? "border-status-red" : ""}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Northgate Dental Group"
              />
            </Field>
            <Field label="Brand owner" htmlFor="brand-owner" required error={ownerNameError}>
              <input
                id="brand-owner"
                required
                aria-invalid={Boolean(ownerNameError)}
                aria-describedby={ownerNameError ? "brand-owner-error" : undefined}
                className={`${inputCls} ${ownerNameError ? "border-status-red" : ""}`}
                value={ownerName}
                onChange={(event) => setOwnerName(event.target.value)}
                placeholder="Full name"
              />
            </Field>
            <Field
              label="Owner email"
              htmlFor="owner-email"
              hint="Account setup is sent separately"
              required
              error={ownerEmailError}
            >
              <input
                id="owner-email"
                type="email"
                required
                aria-invalid={Boolean(ownerEmailError)}
                aria-describedby={ownerEmailError ? "owner-email-error" : undefined}
                className={`${inputCls} ${ownerEmailError ? "border-status-red" : ""}`}
                value={ownerEmail}
                onChange={(event) => setOwnerEmail(event.target.value)}
                placeholder="name@brand.co.uk"
              />
            </Field>
          </div>
        </PageSection>
      )}

      {step === 2 && (
        <PageSection
          title="Tenant & isolation"
          right={<StatusBadge label="Auto-provisioned" tone="blue" />}
        >
          <div className="flex flex-col gap-4 max-w-[680px]">
            <ReadOnlyRow label="Tenant key" value={slug} hint="Generated from the brand name" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Storage region">
                <input className={inputCls} value="London shared cell (lhr)" readOnly />
              </Field>
              <Field label="Lead retention">
                <input className={inputCls} value="365 days · owner can change" readOnly />
              </Field>
            </div>
            <ReadOnlyRow
              label="Encryption key"
              value="Generated per tenant and wrapped under the platform key"
              hint="Not displayed after provisioning"
            />
            <div className="flex items-start gap-2.5 rounded-md border border-default bg-surface-info/40 px-4 py-3">
              <i className="fa-solid fa-shield-halved text-[14px] text-brand-dark-teal mt-0.5" />
              <p className="text-caption text-secondary">
                This brand uses the shared London database with forced row-level security, composite
                tenant constraints, a separate wrapped data key and its own webhook endpoint.
              </p>
            </div>
          </div>
        </PageSection>
      )}

      {step === 3 && (
        <PageSection
          title="Inbound webhook"
          right={<StatusBadge label="Unique to this brand" tone="green" />}
        >
          <div className="flex flex-col gap-4 max-w-[680px]">
            <p className="text-body text-secondary">
              The endpoint and API key are generated when provisioning completes. Both remain
              available to FMC Admins, with the API key encrypted at rest.
            </p>
            <ReadOnlyRow label="Endpoint URL" value="Generated on completion" />
            <ReadOnlyRow label="API key" value="Generated on completion" hint="Admin retrievable" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Accepted contract">
                <input className={inputCls} value="lead.updated · v1" readOnly />
              </Field>
              <Field label="Rate limit">
                <input className={inputCls} value="120 requests/minute" readOnly />
              </Field>
            </div>
          </div>
        </PageSection>
      )}

      {step === 4 && (
        <PageSection title="Outbound delivery" right={<StatusBadge label="Optional" tone="blue" />}>
          <div className="max-w-[720px] flex flex-col gap-4">
            <p className="text-body text-secondary">
              Add the first destination now if its details are available. FMC admins and the brand
              owner can add, replace or remove destinations later.
            </p>
            <div>
              <span className="text-caption font-medium text-primary">Initial delivery method</span>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { id: "none" as const, label: "Set up later", icon: "fa-clock" },
                  { id: "sales_email" as const, label: "Sales email", icon: "fa-envelope" },
                  { id: "webhook" as const, label: "Webhook", icon: "fa-code" },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={outboundKind === option.id}
                    onClick={() => {
                      setOutboundKind(option.id);
                      setError("");
                    }}
                    className={`rounded-md border px-4 py-3 text-left text-body font-semibold transition-colors ${
                      outboundKind === option.id
                        ? "border-brand-red bg-brand-red-tint text-primary"
                        : "border-default text-secondary hover:bg-surface-light"
                    }`}
                  >
                    <i className={`fa-solid ${option.icon} text-[13px] mr-2`} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {outboundKind !== "none" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Destination name" htmlFor="outbound-label" required>
                    <input
                      id="outbound-label"
                      className={inputCls}
                      value={outboundLabel}
                      onChange={(event) => setOutboundLabel(event.target.value)}
                      placeholder="e.g. Sales team"
                    />
                  </Field>
                  <Field
                    label={outboundKind === "sales_email" ? "Sales recipient" : "Webhook URL"}
                    htmlFor="outbound-target"
                    hint={outboundKind === "webhook" ? "HTTPS only" : "One fixed address"}
                    required
                  >
                    <input
                      id="outbound-target"
                      type={outboundKind === "sales_email" ? "email" : "url"}
                      className={inputCls}
                      value={outboundTarget}
                      onChange={(event) => setOutboundTarget(event.target.value)}
                      placeholder={
                        outboundKind === "sales_email" ? "sales@brand.co.uk" : "https://"
                      }
                    />
                  </Field>
                </div>
                {outboundKind === "sales_email" && (
                  <label className="flex items-start gap-2.5 rounded-md border border-default bg-surface-info/40 px-4 py-3 text-caption text-secondary">
                    <input
                      type="checkbox"
                      checked={recipientConfirmed}
                      onChange={(event) => setRecipientConfirmed(event.target.checked)}
                      className="mt-0.5"
                    />
                    I confirm this fixed recipient or distribution list is authorised to process
                    this client's lead PII. Delivery remains inactive until the recipient verifies
                    the address.
                  </label>
                )}
              </>
            )}
          </div>
        </PageSection>
      )}

      {provisioned && (
        <PageSection
          title="Brand provisioned · copy credentials now"
          right={<StatusBadge label="Admin retrievable" tone="green" />}
        >
          <div className="flex flex-col gap-4 max-w-[760px]">
            <ReadOnlyRow label="Tenant ID" value={provisioned.tenantId} />
            <ReadOnlyRow label="HubSpot webhook URL" value={provisioned.webhookUrl} />
            <ReadOnlyRow label={provisioned.apiKeyHeader} value={provisioned.apiKey} />
            {provisioned.outbound?.kind === "webhook" && provisioned.outbound.signingSecret && (
              <ReadOnlyRow
                label="Outbound signing secret"
                value={provisioned.outbound.signingSecret}
                hint="Shown once"
              />
            )}
            <p className="text-caption text-secondary">
              The inbound API key is encrypted and remains available to FMC Admins from the brand
              drawer. No owner account email was sent during brand creation.
            </p>

            {!provisioned.owner.emailVerified && (
              <div className="rounded-md border border-default bg-surface-light px-4 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-body font-semibold text-primary">Owner account setup</h3>
                    <p className="text-caption text-secondary mt-1">
                      Send {provisioned.owner.email} a one-time link to verify their email and
                      choose a password when you are ready to hand over access.
                    </p>
                  </div>
                  <StatusBadge
                    label={setupSent ? "Setup sent" : "Setup pending"}
                    tone={setupSent ? "green" : "amber"}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void sendAccountSetup()}
                  disabled={sendingSetup}
                  className="mt-4 inline-flex h-9 items-center gap-2 rounded-xs bg-brand-red px-4 text-body font-semibold text-on-brand transition-colors hover:bg-brand-red-hover disabled:opacity-50"
                >
                  <i
                    className={`fa-solid ${setupSent ? "fa-rotate-right" : "fa-paper-plane"} text-[12px]`}
                    aria-hidden="true"
                  />
                  {sendingSetup
                    ? "Sending…"
                    : setupSent
                      ? "Resend account setup"
                      : "Send account setup"}
                </button>
              </div>
            )}
          </div>
        </PageSection>
      )}
      {error && <p className="text-caption text-status-red">{error}</p>}

      <div className="flex items-center gap-2.5">
        {provisioned ? (
          <>
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="h-10 px-5 rounded-xs border border-default bg-surface-white text-secondary text-body font-medium hover:bg-surface-light transition-colors"
            >
              Back to brands
            </button>
            <button
              type="button"
              onClick={startAnotherBrand}
              className="h-10 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors inline-flex items-center gap-2"
            >
              <i className="fa-solid fa-plus text-[12px]" aria-hidden="true" />
              Add another brand
            </button>
          </>
        ) : step > 1 ? (
          <button
            onClick={() => goToStep(step - 1)}
            className="h-10 px-5 rounded-xs border border-default text-secondary text-body font-medium hover:bg-surface-white transition-colors"
          >
            Back
          </button>
        ) : null}
        {!provisioned && step < 4 ? (
          <button
            onClick={() => goToStep(step + 1)}
            className="h-10 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors"
          >
            Continue
          </button>
        ) : !provisioned ? (
          <button
            onClick={createBrand}
            disabled={saving || !brandDetailsValid}
            className="h-10 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors"
          >
            {saving ? "Provisioning…" : "Create brand"}
          </button>
        ) : null}
        {!provisioned && (
          <button
            onClick={() => navigate("/admin")}
            className="h-10 px-5 rounded-xs text-tertiary text-body font-medium hover:text-primary transition-colors ml-auto"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
